import React, { useState, useEffect } from 'react';
import { Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import Pagination from '../../../component/Pagination/index.tsx';
import { request } from '../../../api/request.tsx';
import { GetList } from '../../../hook/getList.tsx';

interface LandingPageContent {
  id: string;
  lang: string;
  isActive: boolean;
  bannerUrl: string[];
  bannerMobileUrl: string[];
  branchMetadata: {
    brand: {
      title: string;
      description: string;
      bannerUrl: string[];
      bannerMobileUrl: string[];
    };
  };
  appMetadata: {
    bannerUrl: string[];
    bannerMobileUrl: string[];
  };
}


const ContentLandingPage = () => {
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState('vi');
  const [searchValue, setSearchValue] = useState('');
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());

  const { data, search, loadMore, loading, reLoad } = GetList<any>({
    url: "landing-page-cms",
    isLazy: true,
    dependencies: searchValue,
    params: {
      limit: 10,
      lang: selectedLanguage,
    },
  });

  const list = data?.results || [];
  const totalPages = data?.totalPages || 0;

  useEffect(() => {
    search("");
  }, []);

  const tableData = Array.isArray(list) ? list.map((item: LandingPageContent) => ({
    id: item.id,
    thumbnailUrl: item.branchMetadata?.brand?.bannerUrl?.[0] || '',
    bannerUrl: item.branchMetadata?.brand?.bannerUrl?.[0] || '',
    title: item.branchMetadata?.brand?.title || '',
    appBanner: item.appMetadata?.bannerUrl?.[0] || '',
    isActive: item.isActive,
    fullData: item // Store full item data for editing
  })) : [];

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    search("");
  };

  const handleToggleStatus = async (id: string) => {
    setUpdatingItems(prev => new Set(prev).add(id));
    
    try {
      const response = await request(`landing-page-cms/active`, { id }, 'POST');
      if (response?.status === true) {
        await search("");
        console.log('Status updated successfully and data refreshed');
      } else {
        console.error('Failed to update status:', response?.message);
      }
    } catch (err) {
      console.error('Error toggling status:', err);
    } finally {
      setUpdatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-[pink] mb-6">DANH SÁCH NỘI DUNG LANDING PAGE</h1>
      </div>


      {/* Language Dropdown */}
      <div className="mb-6">
        <Select
          value={selectedLanguage}
          onChange={(value) => handleLanguageChange(value)}
          size="large"
          className="custom-select text-[pink] border-[pink] rounded-md"
        >
          <Select.Option value="vi">Tiếng Việt</Select.Option>
          <Select.Option value="en">English</Select.Option>
          <Select.Option value="ko">한국어</Select.Option>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto mb-4"></div>
              <p className="text-gray-500">Đang tải dữ liệu...</p>
            </div>
          </div>
        ) : tableData.length === 0 ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <p className="text-gray-500 text-lg">Không có dữ liệu</p>
              <p className="text-gray-400 text-sm mt-2">Chưa có nội dung landing page nào</p>
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Ảnh thumb</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Ảnh banner giới thiệu</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Tiêu đề thương hiệu</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">App</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Trạng thái</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Hành động</th>
              </tr>
            </thead>
            <tbody>
            {tableData.map((item, index) => (
              <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                {/* Thumbnail */}
                <td className="px-6 py-4">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                    {item.thumbnailUrl ? (
                      <img 
                        src={item.thumbnailUrl} 
                        alt="Thumbnail" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          const nextSibling = target.nextElementSibling as HTMLElement;
                          target.style.display = 'none';
                          nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">---</span>
                    )}
                    <div className="hidden w-full h-full items-center justify-center">
                      <span className="text-gray-400 text-xs">---</span>
                    </div>
                  </div>
                </td>

                {/* Banner */}
                <td className="px-6 py-4">
                  <div className="w-20 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                    {item.bannerUrl ? (
                      <img 
                        src={item.bannerUrl} 
                        alt="Banner" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          const nextSibling = target.nextElementSibling as HTMLElement;
                          target.style.display = 'none';
                          nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">---</span>
                    )}
                    <div className="hidden w-full h-full items-center justify-center">
                      <span className="text-gray-400 text-xs">---</span>
                    </div>
                  </div>
                </td>

                {/* Title */}
                <td className="px-6 py-4 text-sm text-gray-700">
                  <div className="font-medium">
                    {item.title}
                  </div>
                </td>

                {/* App Banner */}
                <td className="px-6 py-4">
                  <div className="w-20 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                    {item.appBanner ? (
                      <img 
                        src={item.appBanner} 
                        alt="App Banner" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          const nextSibling = target.nextElementSibling as HTMLElement;
                          target.style.display = 'none';
                          nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">---</span>
                    )}
                    <div className="hidden w-full h-full items-center justify-center">
                      <span className="text-gray-400 text-xs">---</span>
                    </div>
                  </div>
                </td>

                {/* Status Toggle */}
                <td className="px-6 py-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={item.isActive}
                      onChange={() => handleToggleStatus(item.id)}
                      disabled={updatingItems.has(item.id)}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500 ${updatingItems.has(item.id) ? 'opacity-50' : ''}`}>
                      {updatingItems.has(item.id) && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>
                  </label>
                </td>

                {/* Actions */}
                <td className="px-6 py-4 text-sm">
                  <div className="flex gap-2">
                    <button 
                      onClick={() => navigate('/admin/content-landing-page', { state: item.fullData })}
                      className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600 transition-colors"
                    >
                      Cập nhật
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        pageCount={totalPages}
        onPageChange={({ selected }) => loadMore(selected + 1)}
        currentPage={1}
        marginPagesDisplayed={2}
        pageRangeDisplayed={2}
      />
    </div>
  );
};

export default ContentLandingPage;